/**
 * Warehouse Optimizer - Core optimization algorithms
 * Ported from Python warehouse_optimizer.py
 */

import type {
  Zip3ReferenceMap,
  ZoneMatrix,
  WarehouseLocation,
  OptimizationParams,
  OptimizationResult,
} from '../types';
import { calculateCoverage } from './coverage';
import { haversineDistance } from '../utils/distance';

interface WarehouseCandidate {
  zip3: string;
  days: number;
  distance: number;
}

/**
 * Find the best warehouse to serve a destination using sophisticated tie-breaking
 */
function findBestWarehouseForDest(
  warehouseZip3s: Set<string>,
  destZip3: string,
  destIdx: number,
  zoneMatrix: ZoneMatrix,
  zip3Reference: Zip3ReferenceMap,
  deliveryGoal: 2 | 3
): WarehouseCandidate {
  const destRef = zip3Reference[destZip3];
  
  if (!destRef) {
    return { zip3: '', days: 99, distance: 9999 };
  }

  const candidates: WarehouseCandidate[] = [];

  // Gather all warehouse candidates with their metrics
  for (const whZip3 of warehouseZip3s) {
    const whIdx = zoneMatrix.origins.indexOf(whZip3);
    if (whIdx === -1) continue;

    const whRef = zip3Reference[whZip3];
    if (!whRef) continue;

    const days = zoneMatrix.matrix[whIdx][destIdx];
    const distance = haversineDistance(
      whRef.lat, whRef.lng,
      destRef.lat, destRef.lng
    );

    candidates.push({ zip3: whZip3, days, distance });
  }

  if (candidates.length === 0) {
    return { zip3: '', days: 99, distance: 9999 };
  }

  // Sort by sophisticated tie-breaking rules
  candidates.sort((a, b) => {
    // 1. Primary: Shortest delivery time (lower is better)
    if (a.days !== b.days) {
      return a.days - b.days;
    }

    // TIED at delivery time - apply hierarchy:

    // Get actual delivery capabilities for tie-breaking
    const aIdx = zoneMatrix.origins.indexOf(a.zip3);
    const bIdx = zoneMatrix.origins.indexOf(b.zip3);
    const aDays = zoneMatrix.matrix[aIdx][destIdx];
    const bDays = zoneMatrix.matrix[bIdx][destIdx];

    // 2. If target is 3-day and we're tied at 3, prefer 2-day capability
    if (deliveryGoal === 3 && a.days === 3) {
      const a_can2day = aDays <= 2;
      const b_can2day = bDays <= 2;
      
      if (a_can2day !== b_can2day) {
        return a_can2day ? -1 : 1; // 2-day capability wins
      }
    }

    // 3. Prefer 1-day capability (express infrastructure indicator)
    const a_can1day = aDays <= 1;
    const b_can1day = bDays <= 1;
    
    if (a_can1day !== b_can1day) {
      return a_can1day ? -1 : 1; // 1-day capability wins
    }

    // 4. Prefer closer distance (0.1 mile tolerance to avoid floating point issues)
    if (Math.abs(a.distance - b.distance) > 0.1) {
      return a.distance - b.distance;
    }

    // 5. Alphabetical fallback (deterministic)
    return a.zip3.localeCompare(b.zip3);
  });

  return candidates[0];
}

/**
 * Get ZIP3s that are reachable within targetDays from an origin
 */
function getDestinationsCoveredBy(
  originZip3: string,
  zoneMatrix: ZoneMatrix,
  targetDays: number
): string[] {
  const originIdx = zoneMatrix.origins.indexOf(originZip3);
  if (originIdx === -1) return [];

  const covered: string[] = [];
  const row = zoneMatrix.matrix[originIdx];

  for (let destIdx = 0; destIdx < row.length; destIdx++) {
    if (row[destIdx] <= targetDays) {
      covered.push(zoneMatrix.destinations[destIdx]);
    }
  }

  return covered;
}

/**
 * Get ZIP3s in specified states
 */
function getZip3sInStates(
  states: string[],
  zip3Reference: Zip3ReferenceMap
): Set<string> {
  const stateSet = new Set(states.map(s => s.toUpperCase()));
  const result = new Set<string>();

  for (const [zip3, data] of Object.entries(zip3Reference)) {
    if (data.state && stateSet.has(data.state.toUpperCase())) {
      result.add(zip3);
    }
  }

  return result;
}

/**
 * Build search pool by applying all constraints
 */
function buildSearchPool(
  zip3Reference: Zip3ReferenceMap,
  zoneMatrix: ZoneMatrix,
  includedStates?: string[],
  excludedStates?: string[],
  candidateZip3s?: string[],
  excludedZip3s?: string[],
  requiredZip3s?: string[]
): Set<string> {
  // Step 1: Start with state-level filtering
  let validZip3s: Set<string>;

  if (includedStates && includedStates.length > 0) {
    // ONLY these states
    validZip3s = getZip3sInStates(includedStates, zip3Reference);
  } else {
    // All ZIP3s that exist in both reference and matrix
    validZip3s = new Set(
      zoneMatrix.origins.filter(z => zip3Reference[z])
    );
  }

  // Step 2: Remove excluded states
  if (excludedStates && excludedStates.length > 0) {
    const excludedZip3sFromStates = getZip3sInStates(excludedStates, zip3Reference);
    excludedZip3sFromStates.forEach(z => validZip3s.delete(z));
  }

  // Step 3: Apply ZIP3-level filtering
  if (candidateZip3s && candidateZip3s.length > 0) {
    // Intersection: candidates that are ALSO in valid states
    const candidateSet = new Set(candidateZip3s);
    validZip3s = new Set([...validZip3s].filter(z => candidateSet.has(z)));
  }

  // Step 4: Remove excluded ZIP3s
  if (excludedZip3s && excludedZip3s.length > 0) {
    excludedZip3s.forEach(z => validZip3s.delete(z));
  }

  // Step 5: Remove required (already selected, don't search for them)
  if (requiredZip3s && requiredZip3s.length > 0) {
    requiredZip3s.forEach(z => validZip3s.delete(z));
  }

  return validZip3s;
}

/**
 * Greedy algorithm: Iteratively pick warehouse that adds most NEW coverage
 * Correctly accounts for overlap between warehouses
 */
export function lazyGreedyOptimize(
  params: OptimizationParams,
  zoneMatrix: ZoneMatrix,
  zip3Reference: Zip3ReferenceMap
): OptimizationResult {
  const { numWarehouses, requiredZip3s = [] } = params;

  // Build search pool with constraints
  const searchPool = buildSearchPool(
    zip3Reference,
    zoneMatrix,
    params.includedStates,
    params.excludedStates,
    params.candidateZip3s,
    params.excludedZip3s,
    requiredZip3s
  );

  // Start with required warehouses
  const selected: WarehouseLocation[] = requiredZip3s.map(zip3 => ({
    zip3,
    lat: zip3Reference[zip3]?.lat,
    lng: zip3Reference[zip3]?.lng,
    city: zip3Reference[zip3]?.city,
    state: zip3Reference[zip3]?.state,
    isRequired: true,
  }));

  // Track already covered destinations
  const alreadyCovered = new Set<string>();

  // Initialize coverage from required warehouses
  requiredZip3s.forEach(wh => {
    getDestinationsCoveredBy(wh, zoneMatrix, 2).forEach(dest => 
      alreadyCovered.add(dest)
    );
  });

  // Iteratively add best warehouse
  const warehousesToFind = numWarehouses - requiredZip3s.length;
  const remainingPool = new Set(searchPool);

  for (let i = 0; i < warehousesToFind; i++) {
    let bestCandidate: string | null = null;
    let bestGain = 0;

    // Find candidate with highest marginal gain
    for (const candidate of remainingPool) {
      const covered = getDestinationsCoveredBy(candidate, zoneMatrix, 2);
      const newlyCovered = covered.filter(d => !alreadyCovered.has(d));

      const gain = newlyCovered.reduce(
        (sum, dest) => sum + (zip3Reference[dest]?.population ?? 0),
        0
      );

      if (gain > bestGain) {
        bestGain = gain;
        bestCandidate = candidate;
      }
    }

    if (!bestCandidate) {
      console.log(`No more candidates found after ${i} warehouses`);
      break;
    }

    // Add best candidate
    selected.push({
      zip3: bestCandidate,
      lat: zip3Reference[bestCandidate]?.lat,
      lng: zip3Reference[bestCandidate]?.lng,
      city: zip3Reference[bestCandidate]?.city,
      state: zip3Reference[bestCandidate]?.state,
      isRequired: false,
      populationCovered: bestGain,
    });

    // Update covered destinations
    getDestinationsCoveredBy(bestCandidate, zoneMatrix, 2).forEach(dest =>
      alreadyCovered.add(dest)
    );

    // Remove from pool
    remainingPool.delete(bestCandidate);
  }

  // Calculate final coverage
  const deliveryGoal = params.deliveryGoal || 2;
  const coverage = calculateCoverage(
    selected.map(w => w.zip3),
    zoneMatrix,
    zip3Reference,
    deliveryGoal
  );

  // Recalculate per-warehouse coverage in final solution
  const finalZip3s = new Set(selected.map(w => w.zip3));
  selected.forEach(wh => {
    // For each destination, find which warehouse is best using sophisticated tie-breaking
    // This warehouse gets credit only for destinations where it's the best option
    let warehouseCredit = 0;

    for (let destIdx = 0; destIdx < zoneMatrix.destinations.length; destIdx++) {
      const dest = zoneMatrix.destinations[destIdx];
      
      const bestWarehouse = findBestWarehouseForDest(
        finalZip3s,
        dest,
        destIdx,
        zoneMatrix,
        zip3Reference,
        deliveryGoal
      );

      // Credit this warehouse if it won AND meets delivery goal
      if (bestWarehouse.zip3 === wh.zip3 && bestWarehouse.days <= deliveryGoal) {
        warehouseCredit += zip3Reference[dest]?.population ?? 0;
      }
    }

    wh.populationCovered = warehouseCredit;
  });

  return {
    warehouses: selected,
    coverage,
    message: `Found ${selected.length} warehouses using lazy greedy algorithm`,
    constraintsSummary: {
      requiredCount: requiredZip3s.length,
      newCount: selected.length - requiredZip3s.length,
      eligibleLocations: searchPool.size,
      statesSearched: params.includedStates?.join(', ') || 'All',
      statesExcluded: params.excludedStates?.length ?? 0,
      zip3sIncluded: params.candidateZip3s ? `${params.candidateZip3s.length} specified` : 'All',
      zip3sExcluded: params.excludedZip3s?.length ?? 0,
    },
  };
}

/**
 * Main optimization entry point
 */
export function optimize(
  params: OptimizationParams,
  zoneMatrix: ZoneMatrix,
  zip3Reference: Zip3ReferenceMap
): OptimizationResult {
  // Validate params
  if (params.numWarehouses < 1 || params.numWarehouses > 20) {
    throw new Error('Number of warehouses must be between 1 and 20');
  }

  const requiredCount = params.requiredZip3s?.length ?? 0;
  if (requiredCount > params.numWarehouses) {
    throw new Error(
      `Required warehouses (${requiredCount}) cannot exceed total (${params.numWarehouses})`
    );
  }

  return lazyGreedyOptimize(params, zoneMatrix, zip3Reference);
}

